import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Modal,
  Fade,
  Grid,
  Backdrop,
  Typography,
  CircularProgress,
  List,
  ListItem,
  IconButton,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useGetAutomateLiteApiTokenLazyQuery } from "../../../generated";
import LoadingButton from "@mui/lab/LoadingButton";

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 486,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

interface DownloadScriptModalProps {
  arn: string;
  region: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Determines the current environment by extracting it from the hostname.
 * Returns the environment if it is one of "app", "dev", or "uat".
 * Otherwise, it returns "N/A".
 */
const determineEnvironment = (): string => {
  const [env] = window.location.hostname.split(".");
  return ["app", "dev", "uat"].includes(env) ? env : "uat";
};

/**
 * Makes an API call to fetch the presigned URL for downloading the script.
 * @param token - API token for authorization.
 * @param env - Current environment.
 * @returns The presigned URL, or undefined if the request fails.
 */
const fetchPresignedUrl = async (
  token: string,
  env: string
): Promise<string | undefined> => {
  const apiUrl = `https://api.${env}.6pillars.ai/automatelite/download-script`;
  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      console.error(`Failed to download script: ${response.statusText}`);
      return;
    }
    const data = await response.json();
    if (data.status === 1 && data.url) {
      return data.url;
    } else {
      console.error("Unexpected response format or download failed.", data);
      return;
    }
  } catch (error) {
    console.error("Download script error:", error);
    return;
  }
};

const DownloadScriptModal: React.FC<DownloadScriptModalProps> = ({
  arn,
  region,
  open,
  onClose,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [getApiToken] = useGetAutomateLiteApiTokenLazyQuery({
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
  });
  const [toastOpen, setToastOpen] = useState(false);
  const navigate = useNavigate();

  const handleDownloadAndNavigate = () => {
    handleDownload();
  };

  useEffect(() => {
    if (!open) setLoading(false);
  }, [open]);

  /**
   * Triggers the file download using a hidden link.
   * @param url - The presigned URL for the downloadable file.
   */
  const downloadScript = (url: string) => {
    if (!url) {
      setError("Download link is not available.");
      return;
    }
    const link = document.createElement("a");
    link.href = url;
    link.download = "download_findings.py";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /**
   * Handles the download process:
   * 1. Retrieves an API token using the provided ARN.
   * 2. Determines the current environment.
   * 3. Fetches the presigned URL.
   * 4. Initiates the file download.
   */
  const handleDownload = () => {
    // Reset any existing errors
    setError(null);
    setLoading(true);
    getApiToken({
      fetchPolicy: "network-only",
      variables: { arn, region },
      context: { apiName: "template_scanner" },
      onCompleted: async (data) => {
        const tokenData = data?.getAutomateLiteApiToken;
        if (tokenData?.msg === "success" && tokenData?.apiToken) {
          const apiToken = tokenData.apiToken;
          let env = determineEnvironment();
          const presignedUrl = await fetchPresignedUrl(apiToken, env);
          if (presignedUrl) {
            setToastOpen(true);
            downloadScript(presignedUrl);
            setLoading(false);
            setTimeout(() => {
              navigate("/rules");
            }, 5000); 
            onClose();
          } else {
            setLoading(false);
            setError("Failed to retrieve the download link.");
          }
        } else {
          setLoading(false);
          setError(tokenData?.error || "Failed to retrieve API token.");
          console.error(tokenData?.error);
        }
      },
      onError: (err) => {
        console.error("API token retrieval failed", err);
        setLoading(false);
        setError("Error retrieving API token.");
      },
    });
  };

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 500 } }}
      aria-labelledby="script-modal-title"
      aria-describedby="script-modal-description"
    >
      <Fade in={open}>
        <Box 
          sx={{ 
            ...modalStyle, 
            p: 4, 
            maxHeight: "80vh", 
            overflowY: "auto"
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={11}>
              <Typography id="script-modal-title" variant="h6" fontWeight="bold">
                Python Script for AWS Security Hub Upload
              </Typography>
            </Grid>
            <Grid item xs={1} sx={{ textAlign: "right" }}>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Grid>
  
            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}
  
            <Grid item xs={12}>
              <Typography id="script-modal-description">
                ✅ <strong>Instructions for Uploading Findings to 6pillars:</strong>
              </Typography>
  
              <List dense>
                <ListItem>
                  <Typography variant="subtitle1" fontWeight="bold">1. Download the Script</Typography>
                </ListItem>
                <ListItem>
                  <Typography>- Click <strong>Download</strong> to get the Python script (valid for 24 hours).</Typography>
                </ListItem>
  
                <ListItem>
                  <Typography variant="subtitle1" fontWeight="bold">2. Set Up AWS Credentials</Typography>
                </ListItem>
                <ListItem>
                  <Typography>- Retrieve AWS credentials for the correct account.</Typography>
                </ListItem>
                <ListItem>
                  <Typography>- Configure your terminal by running:</Typography>
                </ListItem>
                <ListItem sx={{ pl: 4 }}>
                  <Typography sx={{ fontFamily: "monospace" }}>aws configure</Typography>
                </ListItem>
                <ListItem>
                  <Typography>- Enter Access Key, Secret Key, Region, and Output Format.</Typography>
                </ListItem>
  
                <ListItem>
                  <Typography variant="subtitle1" fontWeight="bold">3. Run the Script</Typography>
                </ListItem>
                <ListItem>
                  <Typography>- Execute the script:</Typography>
                </ListItem>
                <ListItem sx={{ pl: 4 }}>
                  <Typography sx={{ fontFamily: "monospace" }}>python download_findings.py</Typography>
                </ListItem>
                <ListItem>
                  <Typography>- ✅ The script will handle uploading the findings to <b>6pillars.</b></Typography>
                </ListItem>
              </List>
  
              <Alert severity="warning" sx={{ mt: 2 }}>
                👉 <strong>Important:</strong> Use correct AWS credentials to avoid uploading to the wrong account.
              </Alert>
            </Grid>
  
            <Grid item xs={12} sx={{ textAlign: "right" }}>
              <LoadingButton
                variant="contained"
                onClick={handleDownloadAndNavigate}
                disabled={loading}
                loading={loading}
                color="secondary"
                loadingIndicator={<CircularProgress color="inherit" size={22} />}
              >
                Download Script
              </LoadingButton>
            </Grid>
          </Grid>
        </Box>
      </Fade>
    </Modal>
    {/* Toast Notification */}
    <Snackbar
      open={toastOpen}
      onClose={() => setToastOpen(false)}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <MuiAlert onClose={() => setToastOpen(false)} severity="success" sx={{ width: '100%' }}>
        You will get your Review & Fix page populated once you run the script.
      </MuiAlert>
    </Snackbar>
    </>
)}
export default DownloadScriptModal